import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { showToast } from '../toastMessage/ToastMessage';
import { decryptData } from '@/components/reusable/crypto/Cipher';
import Cookies from 'js-cookie';
import { toWords } from '../transactionbill/toWordsConfig';

const filterIdOrReferenceId = (data, hiddenKey) => {
  return data?.map((filterData) => {
    return Object.fromEntries(
      Object.entries(filterData).filter(([key, _]) => key !== hiddenKey),
    );
  });
};

export const generateBill = (
  filteredTableData,
  columns,
  date,
  title,
  voucherNumber,
  paidTo,
  totalDrAmount,
  totalCrAmount,
) => {
  if (title === 'Journal') {
    generateBill2(
      filteredTableData,
      columns,
      date,
      title,
      voucherNumber,
      paidTo,
      totalDrAmount,
      totalCrAmount,
    );
  } else {
    generateBill3(
      filteredTableData,
      columns,
      date,
      title,
      voucherNumber,
      paidTo,
      totalDrAmount,
      totalCrAmount,
    );
  }
};

export const generateBill2 = (
  filteredTableData,
  columns,
  date,
  title,
  voucherNumber,
  paidTo,
  totalDrAmount,
  totalCrAmount,
) => {
  const userData = decryptData(localStorage.getItem('userData'));
  const branchAddress = Cookies.get('branchAddress');
  const branchName = Cookies.get('branchName');

  if (!columns?.length) {
    showToast('No data to export', 'info');
    return;
  }

  const doc = new jsPDF({
    orientation: columns?.length > 10 ? 'landscape' : 'portrait',
    compress: true,
  });

  filteredTableData = filterIdOrReferenceId(filteredTableData, 'id');

  const companyDetails = {
    title: `${userData?.companyName || 'Company Name'}`,
    branchInfo: `${branchName || 'Company Branch Name'}, ${
      branchAddress || 'Branch Address'
    }`,
    phone: `+977 ${userData?.companyNumber || 'Phone Number'}`,
    email: `${userData?.companyEmail || 'Email Address'}`,
    date: date,
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginTop = 20; // Top margin for header
  const lineHeight = 7; // Vertical spacing between lines

  // Calculate the total of the last column (example: amount or price)
  const totalAmount = filteredTableData.reduce((total, item) => {
    const amount = parseFloat(item?.amount) || 0; // replace 'amount' with the actual field name
    return total + amount;
  }, 0);

  // Table Data
  const tableHeaders = [columns?.map((column) => column?.title)];
  const tableRows = filteredTableData.map((item) =>
    columns?.map((column) => item[column.dataIndex] ?? ''),
  );

  // Add total row
  // const totalRow = new Array(columns.length - 1).fill(''); // Empty cells for all but last column
  // totalRow?.push(`Total: ${totalAmount.toFixed(3)}`); // Add total in the last column
  // tableRows?.push(totalRow);

  if (title === 'Journal') {
    const totalRow = new Array(columns.length).fill(''); // Fill with empty cells
    totalRow[columns.length - 4] = `Total:`; // Second last column
    totalRow[columns.length - 3] = ` ${totalDrAmount?.toFixed(2)}`; // Second last column
    totalRow[columns.length - 2] = `${totalCrAmount?.toFixed(2)}`; // Last column
    tableRows.push(totalRow);
  }

  doc.autoTable({
    head: tableHeaders,
    body: tableRows,
    margin: { left: 15, right: 15 },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      textColor: 0, // Uniform text color
      fillColor: [255, 255, 255], // Uniform background color
      fontStyle: 'normal',
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    didParseCell: (data) => {
      if (title === 'Journal' && data.row.index === tableRows.length - 1) {
        // Apply styles for the "Total" row
        data.cell.styles.textColor = [0, 0, 0]; // Black text
        data.cell.styles.fillColor = [220, 220, 255]; // Light blue background
        data.cell.styles.fontStyle = 'bold'; // Bold font
      }
    },
    didDrawPage: (data) => {
      const pageHeight = doc.internal.pageSize.height;
      const footerY = pageHeight - 30; // Y-position for footer

      // Draw the header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(companyDetails.title, pageWidth / 2, marginTop, {
        align: 'center',
      });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(
        companyDetails.branchInfo,
        pageWidth / 2,
        marginTop + lineHeight,
        {
          align: 'center',
        },
      );
      doc.text(
        companyDetails.phone,
        pageWidth / 2,
        marginTop + 2 * lineHeight,
        {
          align: 'center',
        },
      );
      doc.text(
        companyDetails.email,
        pageWidth / 2,
        marginTop + 3 * lineHeight,
        {
          align: 'center',
        },
      );
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `Date: ${companyDetails?.date}`,
        pageWidth - 15,
        marginTop + 32,
        {
          align: 'right',
        },
      );
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, marginTop + 5 * lineHeight, {
        align: 'center',
      });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      // doc.text(
      //   `VchNo: ${voucherNumber}`,
      //   15,
      //   marginTop + 6 * lineHeight + 5,
      //   {
      //     align: 'left',
      //   },
      // );
      if (title === 'Payment' || title === 'Receipt') {
        // Rendering the "Received with thanks from mr/mrs" text
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(
          `${title === 'Payment' ? `Paid with thanks to Mr./Mrs.: ${paidTo}` : `Received with thanks from Mr./Mrs.: ${paidTo}`}`,
          15,
          marginTop + 6 * lineHeight + 5,
          { align: 'left' },
        );

        // Rendering the "The sum of rupees" text
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(
          `The sum of rupees: ${totalAmount}`,
          15,
          marginTop + 7 * lineHeight + 5,
          { align: 'left' },
        );
      }
      if (title === 'Contra') {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(
          `The sum of rupees: ${totalAmount}`,
          15,
          marginTop + 6 * lineHeight + 5,
          { align: 'left' },
        );
      }

      // Add footer text arranged horizontally
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Calculate positions for horizontal alignment
      const preparedByX = 15; // Starting position for "Prepared by"
      const signatureX = pageWidth - 80; // Position towards the right for "Signature"
      const footerYHorizontal = data.cursor.y + 20; // Y-position below the table and line

      doc.text(
        'Received by: ______________________',
        preparedByX,
        footerYHorizontal,
        {
          align: 'left',
        },
      );

      // doc.text(
      //   'Approved by: ______________________',
      //   signatureX,
      //   footerYHorizontal,
      //   {
      //     align: 'left',
      //   },
      // );

      const footerText = 'DipArtha';
      const footerTextWidth = doc.getTextWidth(footerText);
      doc.text(
        footerText,
        (pageWidth - footerTextWidth) / 2, // Center the text horizontally
        pageHeight - 10, // Position it near the bottom of the page
      );
    },
    margin:
      title === 'Contra'
        ? { top: marginTop + 7 * lineHeight }
        : title === 'Journal'
          ? { top: marginTop + 6 * lineHeight }
          : { top: marginTop + 8 * lineHeight },
  });
  if (title === 'Journal') {
    const totalRow = new Array(columns.length).fill(''); // Fill with empty cells
    totalRow[columns.length - 2] = `Total Dr: ${totalDrAmount?.toFixed(2)}`; // Second last column
    totalRow[columns.length - 1] = `Total Cr: ${totalCrAmount?.toFixed(2)}`; // Last column
    tableRows.push(totalRow);
  }

  // Open PDF in new tab
  doc.output('dataurlnewwindow');
};

export const generateBill3 = (
  filteredTableData,
  columns,
  date,
  title,
  voucherNumber,
  paidTo,
  totalDrAmount,
  totalCrAmount,
) => {
  const userData = decryptData(localStorage.getItem('userData'));

  if (!columns?.length) {
    showToast('No data to export', 'info');
    return;
  }

  const doc = new jsPDF({
    orientation: columns?.length > 10 ? 'landscape' : 'portrait',
    compress: true,
  });

  filteredTableData = filterIdOrReferenceId(filteredTableData, 'id');

  const companyDetails = {
    title: `${userData?.companyName || 'Company Name'}`,
    branchInfo: `${userData?.branchName || 'Company Branch Name'}, ${
      userData?.branchAddress || 'Branch Address'
    }`,

    phoneEmail: `Phone : +977 ${userData?.branchNumber || 'Phone Number'}     Email : ${userData?.branchEmail || 'Email Address'}`,
    date: date,
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginTop = 20; // Top margin for header
  const lineHeight = 7; // Vertical spacing between lines
  let footerYHeight = 0;

  const tableRows = filteredTableData.map((item) =>
    columns?.map((column) => item[column.dataIndex] ?? ''),
  );

  const total = filteredTableData.reduce(
    (acc, item) => acc + Number(item?.amount || item.dramount),
    0,
  );

  let dataToShow = [];
  if (title === 'Payment') {
    dataToShow = [
      {
        key: 0,
        particular: paidTo?.split('➤')[0] || paidTo,
        debitAmount: 0,
        creditAmount: total,
      },
      ...filteredTableData?.map((item, index) => {
        const debitAmount = item?.amount;
        const creditAmount = 0;
        const particular = item?.payFrom?.split('➤')[0] || item?.payFrom;
        return {
          key: index + 1,
          particular,
          debitAmount,
          creditAmount,
        };
      }),
    ];
  } else if (title === 'Contra') {
    dataToShow = [
      {
        key: 0,
        particular: filteredTableData[0]?.from?.split('➤')[0],
        debitAmount: 0,
        creditAmount: filteredTableData[0]?.amount,
      },
      {
        key: 1,
        particular: filteredTableData[0]?.to?.split('➤')[0],
        debitAmount: filteredTableData[0]?.amount,
        creditAmount: 0,
      },
    ];
  } else if (title === 'Receipt') {
    dataToShow = [
      {
        key: 0,
        particular: paidTo?.split('➤')[0] || paidTo,
        debitAmount: 0,
        creditAmount: total,
      },
      ...filteredTableData?.map((item, index) => {
        const debitAmount = item?.amount;
        const creditAmount = 0;
        const particular = item?.receiveTo?.split('➤')[0] || item?.receiveTo;
        return {
          key: index + 1,
          particular,
          debitAmount,
          creditAmount,
        };
      }),
    ];
  } else if (title === 'Journal') {
    dataToShow = [
      // {
      //   key: 0,
      //   particular: paidTo?.split('➤')[0] || paidTo,
      //   debitAmount: 0,
      //   creditAmount: total,
      // },
      ...filteredTableData?.map((item, index) => {
        const debitAmount = Number(item?.dramount);
        const creditAmount = Number(item?.cramount);
        const particular = item?.particular?.split('➤')[0] || item?.particular;
        return {
          key: index + 1,
          particular,
          debitAmount,
          creditAmount,
        };
      }),
    ];
  }
  const totalData = {
    particular: ' ',
    debitAmount: total,
    creditAmount: total,
  };

  doc.autoTable({
    // head: tableHeaders,
    // body: tableRows,
    margin: { left: 15, right: 15 },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      textColor: 0, // Uniform text color
      fillColor: [255, 255, 255], // Uniform background color
      fontStyle: 'normal',
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: 0,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    didDrawPage: (data) => {
      const pageHeight = doc.internal.pageSize.height;

      // Draw the header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(companyDetails.title, pageWidth / 2, marginTop, {
        align: 'center',
      });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(
        companyDetails.branchInfo,
        pageWidth / 2,
        marginTop + lineHeight,
        {
          align: 'center',
        },
      );
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(
        companyDetails.phoneEmail,
        pageWidth / 2,
        marginTop + 2 * lineHeight,
        {
          align: 'center',
        },
      );

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, marginTop + 5 * lineHeight, {
        align: 'center',
      });

      // Rectangle boxes for the voucher
      doc.rect(15, 50, pageWidth - 30, 8);
      doc.rect(15, 58, pageWidth - 30, 8);
      doc.rect(15, 66, pageWidth - 30, 8);
      doc.rect(15, 74, pageWidth - 30, (dataToShow?.length + 1) * 8);
      doc.rect(15, 74 + (dataToShow?.length + 1) * 8, pageWidth - 30, 8);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, marginTop + 5 * lineHeight, {
        align: 'center',
      });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Voucher No. : ${voucherNumber}`, 18, 65);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Date : ${companyDetails?.date}`, pageWidth - 43, 65);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Particulars`, 18, 73);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Debit`, pageWidth - 80, 73);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Credit`, pageWidth - 45, 73);

      // Rendering the "The sum of rupees" text
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      dataToShow?.forEach((item, index) => {
        doc.text(`${item.particular}`, 18, 73 + (index + 1) * 8);
        doc.text(
          `${item.debitAmount === 0 ? '' : item.debitAmount}`,
          pageWidth - 80,
          73 + (index + 1) * 8,
        );
        doc.text(
          `${item.creditAmount === 0 ? '' : item?.creditAmount}`,
          pageWidth - 45,
          73 + (index + 1) * 8,
        );
      });
      let counter = 1;
      const tableDataY = 73 + dataToShow?.length * 8;

      doc.setFontSize(12);
      doc.line(
        pageWidth / 2 + 20,
        tableDataY + 2,
        pageWidth / 2 + 50,
        tableDataY + 2,
      );
      doc.line(
        pageWidth / 2 + 55,
        tableDataY + 2,
        pageWidth / 2 + 85,
        tableDataY + 2,
      );

      const getY = (flag) => {
        if (flag) counter++;
        return tableDataY + counter * 8;
      };

      doc.text(`${totalData.particular}`, 18, tableDataY + 8);
      doc.text(
        `${totalData.debitAmount === 0 ? '' : totalData.debitAmount}`,
        pageWidth - 80,
        getY(),
      );
      doc.text(
        `${totalData.creditAmount === 0 ? '' : totalData?.creditAmount}`,
        pageWidth - 45,
        getY(),
      );

      const totaltoWords = toWords.convert(total?.toFixed(3));
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`NRs. ${totaltoWords}`, 18, getY(true));
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      {
        title === 'Payment' ||
          (title === 'Receipt' &&
            doc.text(
              `${title === 'Payment' ? `Paid with thanks to Mr./Mrs.: ${paidTo?.split('➤')[0] || filteredTableData[0]?.from?.split('➤')[0]}` : `Received with thanks from Mr./Mrs.: ${paidTo?.split('➤')[0] || filteredTableData[0]?.from?.split('➤')[0]} `}`,
              18,
              getY(true),
              { align: 'left' },
            ));
      }
      footerYHeight = tableDataY + counter * 8;

      // Add footer text arranged horizontally
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Calculate positions for horizontal alignment
      const preparedByX = 15; // Starting position for "Prepared by"
      const signatureX = pageWidth - 80; // Position towards the right for "Signature"
      const footerYHorizontal = footerYHeight + 40; // Y-position below the table and line

      doc.text(
        '(Prepared by: ______________________)',
        preparedByX,
        footerYHorizontal,
        {
          align: 'left',
        },
      );
      doc.text('(Accountant)', pageWidth / 2, footerYHorizontal, {
        align: 'left',
      });
      doc.text('(Prop._________)', pageWidth - 40, footerYHorizontal, {
        align: 'left',
      });

      const footerText = 'DipArtha';
      const footerTextWidth = doc.getTextWidth(footerText);
      doc.text(
        footerText,
        (pageWidth - footerTextWidth) / 2, // Center the text horizontally
        pageHeight - 10, // Position it near the bottom of the page
      );
    },
    margin:
      title === 'Contra'
        ? { top: marginTop + 7 * lineHeight }
        : title === 'Journal'
          ? { top: marginTop + 6 * lineHeight }
          : { top: marginTop + 8 * lineHeight },
  });

  // Open PDF in new tab
  doc.output('dataurlnewwindow');
};
